import { fetchFeatureFlags } from '@app/services/featureFlagService'
import { clientNameAtom } from '@app/state/clientsAtom'
import {
  featureFlagsAtom,
  featureFlagsErrorAtom,
  featureFlagsLoadingAtom,
} from '@app/state/featureFlagAtoms'
import { UseFeatureFlagsResponse, FeatureFlagKey } from '@app/types'
import { useQuery } from '@tanstack/react-query'
import { useAtom, useSetAtom, useAtomValue } from 'jotai'
import { useEffect } from 'react'

export const useFeatureFlags = (): UseFeatureFlagsResponse => {
  // Atoms
  const setFeatureFlags = useSetAtom(featureFlagsAtom)
  const setFeatureFlagsError = useSetAtom(featureFlagsErrorAtom)
  const setFeatureFlagsLoading = useSetAtom(featureFlagsLoadingAtom)
  const [selectedClient] = useAtom(clientNameAtom)

  const {
    data: featureFlagData,
    isPending: isFeatureFlagsLoading,
    isError: isFeatureFlagsError,
    error: featureFlagsError,
    refetch: fetchFeatureFlagsForClient,
  } = useQuery({
    queryKey: ['auth', 'featureFlags', selectedClient],
    queryFn: fetchFeatureFlags,
    enabled: !!selectedClient,
  })

  // Sync feature flags to atoms
  useEffect(() => {
    setFeatureFlags(featureFlagData?.feature_flags ?? [])
  }, [featureFlagData, setFeatureFlags])

  useEffect(() => {
    setFeatureFlagsLoading(isFeatureFlagsLoading)
  }, [isFeatureFlagsLoading, setFeatureFlagsLoading])

  useEffect(() => {
    setFeatureFlagsError(featureFlagsError as Error | null)
  }, [featureFlagsError, setFeatureFlagsError])

  const useFeatureFlag = (flagKey: FeatureFlagKey): boolean => {
    const flagsFromAtom = useAtomValue(featureFlagsAtom)
    const flags = featureFlagData?.feature_flags ?? flagsFromAtom
    return flags.find((flag) => flag.feature === flagKey)?.enabled ?? false
  }

  const useMultipleFeatureFlags = (flagKeys: FeatureFlagKey[]) => {
    const flagsFromAtom = useAtomValue(featureFlagsAtom)
    const flags = featureFlagData?.feature_flags ?? flagsFromAtom
    const result: Record<FeatureFlagKey, boolean> = {} as Record<FeatureFlagKey, boolean>

    flagKeys.forEach((key) => {
      result[key] = flags.find((flag) => flag.feature === key)?.enabled ?? false
    })

    return result
  }

  return {
    featureFlags: featureFlagData?.feature_flags ?? [],
    isFeatureFlagsLoading,
    isFeatureFlagsError,
    featureFlagsError: featureFlagsError as Error | null,
    fetchFeatureFlagsForClient,
    useFeatureFlag,
    useMultipleFeatureFlags,
  }
}
